import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Moon } from "lucide-mui";
import { Link } from "react-router-dom";
import type { OrgDashboardTenant, OrgLiveRun, OrgRecentRun } from "../../../api/client.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import { TenantAvatar } from "../../TenantAvatar.tsx";
import { agentTypeIcon, agentTypeLabel, liveActivity, runLink } from "./agentActivity.tsx";
import LiveDot from "../../LiveDot.tsx";
import EmptyState from "../../EmptyState.tsx";

interface WorkingNowPanelProps {
  live: OrgLiveRun[];
  /** The newest finished run, for "last run finished 2h ago" when nothing runs. */
  lastFinished?: OrgRecentRun;
  tenants: Map<string, OrgDashboardTenant>;
  showTenant: boolean;
}

/** The agents running right now, each with the step it is on (its search, the site it reads). */
export default function WorkingNowPanel({ live, lastFinished, tenants, showTenant }: WorkingNowPanelProps) {
  return (
    <Panel
      fill
      disablePadding
      title={
        <>
          {live.length > 0 && (
            <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", mr: 1, mb: "2px" }}>
              <LiveDot />
            </Box>
          )}
          Working now
        </>
      }
      count={live.length || undefined}
      subtitle="What your agents are doing at this moment"
    >
      {live.length === 0 ? (
        <EmptyState
          icon={<Moon />}
          title="No agent is running"
          description={
            lastFinished && (
              <>
                Last run finished <RelativeTime value={lastFinished.completed_at ?? lastFinished.created_at} />
              </>
            )
          }
        />
      ) : (
        <Stack>
          {live.map((run) => (
            <LiveRunRow
              key={run.id}
              run={run}
              tenant={tenants.get(run.tenant_id)}
              showTenant={showTenant}
            />
          ))}
        </Stack>
      )}
    </Panel>
  );
}

export function LiveRunRow({
  run,
  tenant,
  showTenant,
}: {
  run: OrgLiveRun;
  tenant?: OrgDashboardTenant;
  showTenant: boolean;
}) {
  const activity = liveActivity(run);
  const to = runLink(run, tenant?.slug);
  return (
    <ButtonBase
      component={to ? Link : "div"}
      to={to}
      disabled={!to}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
        "&:hover": to ? { bgcolor: "action.hover" } : undefined,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          color: "success.main",
          bgcolor: "rgba(var(--mui-palette-success-mainChannel) / 0.12)",
          "& svg": { fontSize: 18 },
        }}
      >
        {agentTypeIcon(run.agent_type)}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            {run.agent_name ?? agentTypeLabel(run.agent_type)}
          </Typography>
          {run.subject && (
            <Typography variant="body2" noWrap sx={{ color: "text.secondary", minWidth: 0 }}>
              · {run.subject}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" sx={{ display: "flex", gap: 0.5, minWidth: 0, mt: 0.25 }} component="div">
          <Box component="span" sx={{ color: "success.main", fontWeight: 500, flexShrink: 0 }}>
            {activity.verb}
            {activity.detail ? (activity.verb === "Reading" ? "" : ":") : "…"}
          </Box>
          {activity.detail && (
            <Box
              component="span"
              sx={{
                color: "text.secondary",
                fontStyle: activity.quote ? "italic" : "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {activity.quote ? `“${activity.detail}”` : activity.detail}
            </Box>
          )}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          {showTenant && tenant && (
            <>
              <TenantAvatar name={tenant.name} logoUrl={tenant.logo_url} size={14} />
              <span>{tenant.name} ·</span>
            </>
          )}
          {run.started_at ? (
            <span>
              started <RelativeTime value={run.started_at} />
            </span>
          ) : (
            <span>queued</span>
          )}
        </Typography>
      </Box>
    </ButtonBase>
  );
}
